// CoandaView Authentication Module
// Handles authentication with CoandaView's own auth system

class Auth {
  constructor() {
    this.token = null;
    this.user = null;
    this.guestToken = null;  // Token for guest access via public links
    this.isGuest = false;
    this.apiBase = window.location.origin.includes('localhost')
      ? 'http://localhost:8082'
      : '';
  }

  // Check if this is a public link access (case parameter in URL)
  isPublicLinkAccess() {
    const params = new URLSearchParams(window.location.search);
    return params.has('case') && !this.isAuthenticated();
  }

  // Get guest token for public link access
  async getGuestAccess(casePath, guestName = null) {
    try {
      const response = await fetch(`${this.apiBase}/api/guest/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_path: casePath, guest_name: guestName })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.guest_token) {
          this.guestToken = data.guest_token;
          this.isGuest = true;
          // Use the provided guest name or the one from server
          const displayName = guestName || data.guest_name || 'Guest';
          this.user = {
            id: data.guest_id,
            name: displayName,
            display_name: displayName,
            email: null,
            is_guest: true,
            session_id: data.session_id,  // Store session_id for collaboration
            role: data.role || 'viewer'
          };
          console.log('Guest access granted:', this.user);
          return true;
        }
      }
      console.warn('Guest access denied:', await response.text());
      return false;
    } catch (error) {
      console.error('Failed to get guest access:', error);
      return false;
    }
  }

  // Show modal for guest to enter their name
  showGuestNameModal() {
    return new Promise((resolve) => {
      // Create modal overlay
      const overlay = document.createElement('div');
      overlay.id = 'guestNameModal';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      `;

      const modal = document.createElement('div');
      modal.style.cssText = `
        background: #1e293b;
        border-radius: 16px;
        padding: 32px;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.1);
      `;

      modal.innerHTML = `
        <div style="text-align: center; margin-bottom: 24px;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" style="margin-bottom: 16px;">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          <h2 style="color: white; margin: 0 0 8px 0; font-size: 1.5rem;">Welcome, Guest!</h2>
          <p style="color: #94a3b8; margin: 0; font-size: 0.9rem;">Enter your name to join the shared session</p>
        </div>
        <div style="margin-bottom: 24px;">
          <input type="text" id="guestNameInput" placeholder="Your name" maxlength="30" style="
            width: 100%;
            padding: 12px 16px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.05);
            color: white;
            font-size: 1rem;
            outline: none;
            box-sizing: border-box;
            transition: border-color 0.2s;
          " />
        </div>
        <div style="display: flex; gap: 12px;">
          <button id="guestCancelBtn" style="
            flex: 1;
            padding: 12px 24px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            background: transparent;
            color: #94a3b8;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.2s;
          ">Cancel</button>
          <button id="guestJoinBtn" style="
            flex: 1;
            padding: 12px 24px;
            border: none;
            border-radius: 8px;
            background: #3b82f6;
            color: white;
            font-size: 1rem;
            cursor: pointer;
            font-weight: 500;
            transition: all 0.2s;
          ">Join Session</button>
        </div>
      `;

      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      const input = document.getElementById('guestNameInput');
      const joinBtn = document.getElementById('guestJoinBtn');
      const cancelBtn = document.getElementById('guestCancelBtn');

      // Focus input
      setTimeout(() => input.focus(), 100);

      // Handle input focus styles
      input.addEventListener('focus', () => {
        input.style.borderColor = '#3b82f6';
      });
      input.addEventListener('blur', () => {
        input.style.borderColor = 'rgba(255, 255, 255, 0.2)';
      });

      // Handle join
      const handleJoin = () => {
        const name = input.value.trim();
        if (name) {
          overlay.remove();
          resolve(name);
        } else {
          input.style.borderColor = '#ef4444';
          input.placeholder = 'Please enter your name';
        }
      };

      joinBtn.addEventListener('click', handleJoin);
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') handleJoin();
      });

      // Handle cancel
      cancelBtn.addEventListener('click', () => {
        overlay.remove();
        resolve(null);
      });

      // Handle escape key
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          overlay.remove();
          resolve(null);
        }
      });
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    this.token = localStorage.getItem('coandacloud_token');
    const userStr = localStorage.getItem('coandacloud_user');

    if (this.token && userStr) {
      try {
        this.user = JSON.parse(userStr);
        return true;
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  // Get current user
  getCurrentUser() {
    if (!this.user) {
      const userStr = localStorage.getItem('coandacloud_user');
      if (userStr) {
        try {
          this.user = JSON.parse(userStr);
        } catch (e) {
          this.user = null;
        }
      }
    }
    return this.user;
  }

  // Get auth token (returns guest token if in guest mode)
  getToken() {
    if (this.isGuest && this.guestToken) {
      return this.guestToken;
    }
    if (!this.token) {
      this.token = localStorage.getItem('coandacloud_token');
    }
    return this.token;
  }

  // Redirect to login page (landing page)
  redirectToLogin() {
    window.location.href = '/landing.html';
  }

  // Verify token is still valid
  async verifyToken() {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${this.apiBase}/api/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          // Update user data with ToS info
          if (result.data && result.data.user) {
            this.user = result.data.user;
            localStorage.setItem('coandacloud_user', JSON.stringify(this.user));
          }
          return true;
        }
      }

      // Token invalid, clear it
      this.logout();
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  // Check if user needs to accept updated ToS
  async checkTosStatus() {
    const user = this.getCurrentUser();
    if (!user || !user.tos_info) return false;

    // Check if ToS acceptance is needed
    if (user.tos_info.needs_acceptance) {
      return true;
    }

    return false;
  }

  // Logout user
  logout() {
    localStorage.removeItem('coandacloud_token');
    localStorage.removeItem('coandacloud_user');
    this.token = null;
    this.user = null;
    this.redirectToLogin();
  }

  // Initialize authentication check
  async init() {
    console.log('Initializing CoandaView authentication...');

    // Check if user is authenticated
    if (this.isAuthenticated()) {
      // Verify token is still valid
      const isValid = await this.verifyToken();
      if (!isValid) {
        console.log('Token invalid, redirecting to login...');
        this.redirectToLogin();
        return false;
      }

      // Check if user needs to accept updated ToS
      const needsTos = await this.checkTosStatus();
      if (needsTos) {
        console.log('User needs to accept updated ToS');
        // Show ToS modal - will be handled by index.html
        if (window.showTosUpdateNotification) {
          window.showTosUpdateNotification();
        }
      }

      console.log('User authenticated:', this.user);
      return true;
    }

    // Not authenticated - check if this is a public link access
    const params = new URLSearchParams(window.location.search);
    const casePath = params.get('case');

    if (casePath) {
      console.log('Attempting guest access for public link:', casePath);

      // First, ask for guest name
      const guestName = await this.showGuestNameModal();
      if (!guestName) {
        // User cancelled - redirect to landing
        console.log('Guest cancelled name entry');
        this.redirectToLogin();
        return false;
      }

      const guestAccess = await this.getGuestAccess(casePath, guestName);
      if (guestAccess) {
        console.log('Guest access granted for public link');
        return true;
      }
      console.log('Guest access denied, this case may not have public link enabled');
      // Show a message instead of redirecting
      this.showGuestAccessDenied();
      return false;
    }

    // No case parameter and not authenticated - redirect to login
    console.log('User not authenticated, redirecting to login...');
    this.redirectToLogin();
    return false;
  }

  // Show guest access denied message
  showGuestAccessDenied() {
    const loadingOverlay = document.getElementById('loadingOverlay');
    if (loadingOverlay) {
      loadingOverlay.innerHTML = `
        <div style="text-align: center; color: white; padding: 40px;">
          <h2 style="margin-bottom: 20px;">Access Denied</h2>
          <p style="margin-bottom: 20px;">This link is not publicly shared or has expired.</p>
          <p style="margin-bottom: 30px;">Please ask the owner to share the case with you, or</p>
          <a href="/landing.html" style="
            background: #3b82f6;
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 500;
          ">Sign in to CoandaCloud</a>
        </div>
      `;
      loadingOverlay.style.display = 'flex';
      loadingOverlay.style.alignItems = 'center';
      loadingOverlay.style.justifyContent = 'center';
    }
  }

  // Get user info for display
  getUserInfo() {
    const user = this.getCurrentUser();
    if (!user) return null;

    const info = {
      name: user.display_name || user.name || user.email || 'Guest',
      email: user.email,
      id: user.id,
      display_name: user.display_name,
      is_admin: user.is_admin || false
    };

    // Include guest-specific info
    if (user.is_guest) {
      info.is_guest = true;
      info.session_id = user.session_id;
      info.role = user.role;
    }

    return info;
  }

  // Add auth header to fetch requests
  getFetchHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Authenticated fetch wrapper
  async authenticatedFetch(url, options = {}) {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token available');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    // Add guest indicator header if in guest mode
    if (this.isGuest) {
      headers['X-Guest-Access'] = 'true';
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized
    if (response.status === 401) {
      if (this.isGuest) {
        console.log('Guest access expired or invalid');
        this.showGuestAccessDenied();
        throw new Error('Guest access denied');
      }
      console.log('Unauthorized request, redirecting to login...');
      this.logout();
      throw new Error('Unauthorized');
    }

    return response;
  }

  // Check if current user is a guest
  isGuestUser() {
    return this.isGuest;
  }
}

// Export singleton instance
window.Auth = new Auth();
